import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Wrench, Globe2, Star, ChevronRight, Zap, CheckCircle2, Sparkles, Handshake, FileCheck2, Headphones, Award } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { fmtNumber, getHT, getTTC } from "@/lib/prices";
import ProductCard from "@/components/ProductCard";
import AddToCartButton from "@/components/AddToCartButton";
import { cdn } from "@/lib/img";
import GoogleReviews from "@/components/GoogleReviews";
import HomeOccasions from "@/components/occasion/HomeOccasions";

const HERO_IMG = "/hero-bg.webp";

const USPS = [
  { icon: ShieldCheck, title: "Garantie 24 mois", desc: "Garantie constructeur étendue sur toute la gamme." },
  { icon: Wrench, title: "Montage & vérif. en atelier", desc: "Chaque machine est préparée dans notre atelier avant livraison." },
  { icon: Headphones, title: "Support technique et pièces", desc: "Pièces détachées disponibles et conseils techniques." },
  { icon: Globe2, title: "Importateur spécialisé", desc: "Partenaire officiel RIPPA." },
];


export default function Home() {
  const [miniPelles, setMiniPelles] = useState([]);
  const [bestSeller, setBestSeller] = useState(null);

  useEffect(() => {
    api.get("/mini-pelles").then((r) => {
      setMiniPelles(r.data);
      const bs = r.data.find((m) => m.best_seller) || r.data.find((m) => m.slug === "rippa-r10");
      setBestSeller(bs);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Mini-pelles neuves et d'occasion à Bourgoin-Jallieu — RIPPA"
        description="Distributeur de mini-pelles RIPPA (730 kg à 3,2 T) à Bourgoin-Jallieu (38300). Vente neuf à prix importateur et mini-pelles d'occasion contrôlées, pièces détachées. Devis rapide."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#072135]" data-testid="hero">
        {/* Background image with slight blur + ken-burns zoom */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={HERO_IMG}
            alt="Mini-pelles RIPPA montées en atelier chez LMB Services"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover object-center scale-110 animate-ken-burns will-change-transform"
          />
          {/* Layer 1: global uniform dim so photo blends into brand tone */}
          <div className="absolute inset-0 bg-[#072135]/40" />
          {/* Layer 2: strong dark panel on the LEFT where text sits (sharp readability) */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#072135] from-0% via-[#072135]/85 via-30% to-transparent to-70%" />
          {/* Layer 3: subtle bottom shade for USP band */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#072135] via-[#072135]/60 to-transparent" />
          {/* Subtle noise/grain for texture */}
          <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        </div>

        {/* Decorative floating badges (visible only on larger screens) */}
        <div className="hidden lg:block absolute top-10 right-10 z-10 animate-fade-up delay-3">
          <div className="bg-white/10 backdrop-blur-md border border-[#D8B86A]/40 rounded-sm px-4 py-2.5 shadow-xl">
            <div className="flex items-center gap-2 text-[#D8B86A] font-mono-tech text-[10px] uppercase tracking-widest font-bold">
              <Sparkles size={14} /> Machines neuves vérifiées
            </div>
          </div>
        </div>
        <div className="hidden lg:block absolute bottom-32 right-24 z-10 animate-fade-up delay-4">
          <div className="bg-[#D8B86A] text-[#072135] rounded-sm px-4 py-2.5 shadow-xl rotate-[-2deg]">
            <div className="flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-widest font-bold">
              <Wrench size={14} /> Montage en atelier
            </div>
          </div>
        </div>

        <div className="relative lmb-container py-24 sm:py-28 lg:py-36">
          <div className="max-w-2xl">
            {/* Eyebrow with mini-icon */}
            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 bg-[#D8B86A] rounded-sm mb-6 animate-fade-up shadow-lg">
              <Zap size={14} className="text-[#072135]" />
              <span className="text-[12px] font-mono-tech uppercase tracking-[0.18em] text-[#072135] font-bold">
                Minipelles RIPPA
              </span>
            </div>

            {/* Big headline */}
            <h1 className="lmb-h1 text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mb-6 animate-fade-up delay-1 leading-[1.02]" style={{ textShadow: "0 2px 12px rgba(7, 33, 53, 0.65)" }}>
              Votre minipelle,
              <br />
              <span className="relative inline-block">
                <span className="text-[#D8B86A]">prête à travailler</span>
                <svg className="absolute left-0 right-0 -bottom-2 w-full" height="12" viewBox="0 0 300 12" preserveAspectRatio="none">
                  <path d="M2 8 Q 75 2 150 6 T 298 5" stroke="#D8B86A" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.5" />
                </svg>
              </span>
            </h1>

            {/* Description */}
            <p className="text-white/90 text-base sm:text-lg max-w-2xl mb-4 leading-relaxed animate-fade-up delay-2" style={{ textShadow: "0 1px 8px rgba(7, 33, 53, 0.6)" }}>
              Découvrez notre gamme de <strong className="text-white">minipelles RIPPA</strong>, montées et vérifiées dans notre atelier pour vous garantir une machine prête à l'emploi.
            </p>
            <p className="text-white/80 text-base sm:text-lg max-w-2xl mb-10 leading-relaxed animate-fade-up delay-2" style={{ textShadow: "0 1px 8px rgba(7, 33, 53, 0.6)" }}>
              Des modèles compacts et performants, un accompagnement personnalisé et un service de proximité pour concrétiser vos projets.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 animate-fade-up delay-3">
              <Link to="/mini-pelles" data-testid="hero-cta-mini" className="group inline-flex items-center justify-center gap-2 px-7 py-4 font-bold uppercase tracking-wide text-sm rounded-sm transition-all bg-[#D8B86A] text-[#072135] hover:bg-white hover:translate-y-[-2px] shadow-2xl">
                Voir nos minipelles
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="#services-section" data-testid="hero-cta-services" className="group inline-flex items-center justify-center gap-2 px-7 py-4 font-bold uppercase tracking-wide text-sm rounded-sm transition-all bg-white/10 backdrop-blur border-2 border-white/30 text-white hover:bg-white hover:text-[#072135] hover:border-white hover:translate-y-[-2px]">
                Découvrir nos services
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            {/* Mini-trust row */}
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 animate-fade-up delay-4">
              <div className="flex items-center gap-1.5 text-white/80 text-xs font-mono-tech uppercase tracking-wider">
                <CheckCircle2 size={14} className="text-[#D8B86A]" /> Garantie 24 mois
              </div>
              <div className="flex items-center gap-1.5 text-white/80 text-xs font-mono-tech uppercase tracking-wider">
                <CheckCircle2 size={14} className="text-[#D8B86A]" /> Partenaire officiel RIPPA
              </div>
              <div className="flex items-center gap-1.5 text-white/80 text-xs font-mono-tech uppercase tracking-wider">
                <CheckCircle2 size={14} className="text-[#D8B86A]" /> SAV & pièces disponibles
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <a
            href="#services-section"
            data-testid="scroll-indicator"
            aria-label="Faire défiler vers le bas"
            className="hidden md:flex absolute left-1/2 -translate-x-1/2 bottom-6 lg:bottom-10 flex-col items-center gap-2 text-white/70 hover:text-[#D8B86A] transition-colors group"
          >
            <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em]">Découvrir</span>
            <span className="relative flex h-10 w-6 items-start justify-center rounded-full border-2 border-white/50 group-hover:border-[#D8B86A] transition-colors">
              <span className="mt-1.5 h-2 w-1 rounded-full bg-white/70 group-hover:bg-[#D8B86A] animate-scroll-dot" />
            </span>
          </a>
        </div>

        <div id="services-section" className="relative border-t border-white/10 bg-[#051826]/70 backdrop-blur">
          <div className="lmb-container py-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {USPS.map((u) => (
              <div key={u.title} data-testid={`usp-${u.title}`} className="flex items-start gap-3 text-white">
                <div className="w-10 h-10 flex-shrink-0 bg-[#D8B86A]/15 border border-[#D8B86A]/40 flex items-center justify-center rounded-sm">
                  <u.icon size={18} className="text-[#D8B86A]" />
                </div>
                <div>
                  <div className="font-heading font-bold text-sm leading-tight">{u.title}</div>
                  <div className="text-xs text-white/60 leading-snug">{u.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEST SELLER R10 */}
      {bestSeller && (
        <section className="lmb-section bg-white" data-testid="best-seller-section">
          <div className="lmb-container">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="absolute -top-4 -left-4 z-10 bg-[#D8B86A] text-[#072135] px-5 py-2 font-heading font-black text-sm uppercase tracking-wider flex items-center gap-2 rounded-sm shadow-lg animate-pulse">
                  <Star size={14} fill="#072135" /> Best-Seller 2025
                </div>
                <div className="aspect-[4/3] overflow-hidden rounded-sm border-2 border-[#072135] bg-white flex items-center justify-center">
                  <img src={cdn(bestSeller.image_principale, 800)} loading="lazy" decoding="async" alt={bestSeller.modele} className="max-w-full max-h-full object-contain p-4 hover:scale-105 transition-transform duration-700" />
                </div>
              </div>
              <div>
                <div className="text-xs font-mono-tech tracking-[0.2em] text-[#D8B86A] uppercase mb-3 flex items-center gap-2">
                  <Zap size={14} className="text-[#D8B86A]" /> — La référence de la gamme
                </div>
                <h2 className="font-heading text-4xl sm:text-6xl font-black tracking-tight text-[#072135] mb-3 leading-[1.05]">
                  {bestSeller.modele}
                </h2>
                <div className="font-heading text-2xl text-[#364757] mb-6">
                  La mini-pelle préférée des particuliers
                </div>
                <p className="text-[#4B5563] mb-6 leading-relaxed text-lg">
                  Compacte, robuste et polyvalente. Idéale pour les chantiers urbains, le paysagisme et les petits travaux d'aménagement.
                </p>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-[#F9F9F9] p-4 border border-gray-200 rounded-sm text-center">
                    <div className="text-[10px] uppercase tracking-wider font-mono-tech text-[#4B5563]">Poids</div>
                    <div className="font-heading text-xl font-black text-[#072135]">{bestSeller.poids_kg} kg</div>
                  </div>
                  <div className="bg-[#F9F9F9] p-4 border border-gray-200 rounded-sm text-center">
                    <div className="text-[10px] uppercase tracking-wider font-mono-tech text-[#4B5563]">Profondeur</div>
                    <div className="font-heading text-xl font-black text-[#072135]">{bestSeller.profondeur_fouille_mm} mm</div>
                  </div>
                  <div className="bg-[#F9F9F9] p-4 border border-gray-200 rounded-sm text-center">
                    <div className="text-[10px] uppercase tracking-wider font-mono-tech text-[#4B5563]">Largeur</div>
                    <div className="font-heading text-xl font-black text-[#072135]">{bestSeller.largeur_mm} mm</div>
                  </div>
                </div>

                <div className="flex items-baseline gap-3 mb-6 pb-6 border-b border-gray-200">
                  <span className="text-sm uppercase tracking-widest text-[#4B5563] font-mono-tech">À partir de</span>
                  <div>
                    <div className="lmb-price text-4xl">{fmtNumber(getTTC(bestSeller))} € TTC</div>
                    <div className="text-xs font-mono-tech text-[#4B5563]">{fmtNumber(getHT(bestSeller))} € HT</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link to={`/mini-pelles/${bestSeller.slug}`} className="lmb-btn-secondary">
                    Voir la fiche complète <ArrowRight size={16} />
                  </Link>
                  <AddToCartButton
                    product={bestSeller}
                    label="Ajouter au panier"
                    testId={`home-add-${bestSeller.slug}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Mini-pelles catalog */}
      <section className="lmb-section bg-[#F9F9F9]" data-testid="home-mini-pelles">
        <div className="lmb-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="text-xs font-mono-tech tracking-[0.2em] text-[#D8B86A] uppercase mb-2">— Catalogue complet</div>
              <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#072135] leading-[1.05]">
                Mini-pelles RIPPA
                <br />
                <span className="text-[#364757] text-3xl sm:text-4xl">de 0,6 T à 3,2 T</span>
              </h2>
            </div>
            <Link to="/mini-pelles" className="lmb-btn-outline self-start md:self-end">
              Voir toute la gamme <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {miniPelles.slice(0, 3).map((m) => <ProductCard key={m.slug} item={m} baseUrl="/mini-pelles" />)}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="home-range-links">
            <span className="text-sm text-[#4B5563] mr-1">Toute la gamme neuve :</span>
            {miniPelles.map((m) => (
              <Link key={m.slug} to={`/mini-pelles/${m.slug}`} className="px-3 py-1.5 rounded-full border border-gray-200 bg-white text-sm font-bold text-[#072135] hover:border-[#072135] transition-colors">
                Mini-pelle {m.modele}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <HomeOccasions />

      {/* POURQUOI LMB SERVICES — Réassurance */}
      <section className="lmb-section bg-[#F9F9F9] border-y border-gray-200" data-testid="why-lmb-section">
        <div className="lmb-container">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-xs font-mono-tech tracking-[0.2em] text-[#D8B86A] uppercase mb-3">— Notre engagement</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#072135] leading-[1.05] mb-5">
              Pourquoi choisir <span className="text-[#2877C2]">LMB SERVICES&nbsp;?</span>
            </h2>
            <p className="text-[#4B5563] text-lg leading-relaxed">
              Mini-pelle neuve ou d'occasion, notre objectif reste le même : vous proposer la meilleure machine selon votre budget, avec un vrai accompagnement avant et après l'achat.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: Handshake, title: "Accompagnement français", desc: "Un interlocuteur dédié de A à Z." },
              { icon: FileCheck2, title: "Fournisseurs sélectionnés", desc: "Usines auditées, qualité contrôlée." },
              { icon: Award, title: "Expertise mini-pelles", desc: "Neuf, occasion et pièces détachées." },
              { icon: Headphones, title: "Assistance avant & après", desc: "Conseil, livraison, suivi, SAV." },
            ].map((item) => (
              <div key={item.title} data-testid={`why-${item.title}`} className="group bg-white p-6 sm:p-7 rounded-sm border border-gray-200 hover:border-[#D8B86A] hover:shadow-lg transition-all flex flex-col gap-3">
                <div className="w-12 h-12 bg-[#D8B86A]/10 border border-[#D8B86A]/30 flex items-center justify-center rounded-sm group-hover:bg-[#D8B86A] transition-colors">
                  <item.icon size={22} className="text-[#072135]" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-heading text-lg font-black text-[#072135] leading-tight mb-1">{item.title}</div>
                  <div className="text-sm text-[#4B5563] leading-snug">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services bento */}
      <section className="lmb-section bg-[#072135] text-white" data-testid="services-bento">
        <div className="lmb-container">
          <div className="text-center mb-14">
            <div className="text-xs font-mono-tech tracking-[0.2em] text-[#D8B86A] uppercase mb-2">— Nos services</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">
              Trois métiers, <span className="text-[#D8B86A]">un seul interlocuteur</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {[
              { title: "Vente", desc: "Mini-pelles RIPPA neuves à prix importateur. Accessoires compatibles inclus.", to: "/mini-pelles" },
              { title: "Occasion", desc: "Mini-pelles d'occasion contrôlées, prix clairs, visibles à Bourgoin-Jallieu.", to: "/mini-pelles-occasion" },
              { title: "Pièces détachées", desc: "Filtres, moteurs, hydraulique : pièces pour mini-pelles payables en ligne.", to: "/pieces-detachees" },
            ].map((s) => (
              <Link key={s.title} to={s.to} data-testid={`service-${s.title}`} className="group bg-[#072135] hover:bg-[#051826] p-10 transition-colors flex flex-col gap-3 min-h-[220px]">
                <h3 className="font-heading text-3xl font-black text-[#D8B86A] flex items-center justify-between">
                  {s.title}
                  <ChevronRight size={20} className="text-white/40 group-hover:text-[#D8B86A] group-hover:translate-x-1 transition-all" />
                </h3>
                <p className="text-white/70 leading-relaxed">{s.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <GoogleReviews />
    </div>
  );
}
